import { useCallback, useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { create, divideDependencies, evaluateDependencies, subtractDependencies } from 'mathjs'

import { ColumnTypes } from '@makyoapp/makyo-sdk/models/Table'
import { ArrowRight16, ChevronSmallDown } from '@makyoapp/makyo-icon/icon'
import { TextArea } from '@makyoapp/makyo-ui'

import { useCreateColumnContext } from '@/context/CreateColumnContext'
import { debounce } from '@/shared/utils'

import { ColumnDd } from './ColumnDd'
import FieldNumber from './FieldNumber'
import { Fld, Lbl } from './shared'

/**
 * @see https://mathjs.org/docs/expressions/syntax.html
 *
 * @note if another valid expression failed to be evalueted,
 * consider replacing the `create` dependencies with `all`
 */
const { evaluate } = create({
  evaluateDependencies,
  divideDependencies,
  subtractDependencies,
})

/**
 * validate expression
 */
function validateExpression(expr: string, vars: string[]) {
  if (!expr.trim()) {
    return true
  }
  try {
    const scope = vars.reduce((acc: Record<string, number>, v) => {
      acc[v] = 1
      return acc
    }, {})

    const evalResult = evaluate(expr, scope)
    return true
  } catch (err) {
    console.error(err)
    return false
  }
}

export default function FieldMath() {
  const { t } = useTranslation()
  const { id, value, setValue } = useCreateColumnContext()
  const currentColumnId = id.split('-').pop()

  const [open, setOpen] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const debounceValidateExpression = useCallback(
    debounce(({ expr, vars }: { expr: string; vars: string[] }) => {
      const isValid = validateExpression(expr, vars)
      setError(isValid ? null : 'Please enter a valid expression')
    }, 450),
    []
  )

  useEffect(() => {
    debounceValidateExpression({
      expr: value.mathOptions.expr,
      vars: value.mathOptions.vars,
    })
  }, [debounceValidateExpression, value.mathOptions.expr, value.mathOptions.vars])

  /**
   * handle input change
   */
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.currentTarget.value
      // preprocess the input to add leading zeros to decimal numbers
      // e.g: `price * .25` -> `price * 0.25`
      .replace(/([+\-*/()]|^)(\s*)\.(\d+)/g, '$1$20.$3')

    // extract variables from config, handling both regular variables and cell references
    // This regex matches:
    // 1. Standard variables: [a-zA-Z_][a-zA-Z0-9_]*
    // 2. Cell references: [A-Z]+[0-9]+
    const newVars =
      newValue
        ?.split(/[\s+\-*/()]+/) // Split by operators and spaces
        .filter((part) => part.match(/^([a-zA-Z_][a-zA-Z0-9_]*|[A-Z]+[0-9]+)$/))
        .filter(Boolean) || []

    const uniqueVars = [...new Set(newVars)]

    setValue({
      mathOptions: {
        ...value.mathOptions,
        expr: newValue,
        vars: uniqueVars,
        columns: uniqueVars.map((_, idx) => {
          const columns = value.mathOptions.columns || []
          if (columns[idx]) {
            return columns[idx]
          }
          return ''
        }),
      },
    })
  }

  if (!value.mathOptions) return null

  const { expr, vars, columns } = value.mathOptions
  const hasExpr = !!expr.trim().length

  return (
    <div>
      <Fld>
        <Lbl>{t('Configuration')}</Lbl>
        <div className='flex-1 flex flex-col'>
          <TextArea
            id={`${id}-math-expr`}
            value={expr}
            placeholder={`Enter your formula, e.g. "price / 100"`}
            rows={2}
            className={`w-full min-h-0! font-mono tracking-tight`}
            onChange={handleInputChange}
          />
          <div className='mk-input-error min-h-[18px]'>{error}</div>
        </div>
      </Fld>

      {hasExpr && vars.length > 0 && (
        <Fld>
          <Lbl>{t('Replacements')}</Lbl>
          <div className='flex-1 space-y-2'>
            {vars.map((variable, index) => (
              <div key={variable} className='flex items-center gap-2'>
                <div
                  title={variable}
                  className='w-[40%] truncate font-mono rounded-sm p-2 bg-zinc-200 dark:bg-slate-900 flex items-center justify-between'
                >
                  <span>{variable}</span>
                  <ArrowRight16 width={14} height={14} className='text-light' />
                </div>
                <ColumnDd
                  id={`${id}-math-column-${index}`}
                  placeholder='Select column'
                  selected={columns?.[index] || ''}
                  onChange={(opt) => {
                    const newColumns = [...(value.mathOptions.columns || [])]
                    newColumns[index] = opt?.id || ''
                    setValue({
                      mathOptions: { ...value.mathOptions, columns: newColumns },
                    })
                  }}
                  types={[
                    ColumnTypes.Number,
                    ColumnTypes.Math,
                    ColumnTypes.SingleCell,
                    ColumnTypes.Lookup,
                    ColumnTypes.Function,
                  ]}
                  currentColumnId={currentColumnId}
                />
              </div>
            ))}
          </div>
        </Fld>
      )}

      <div className='min-w-0 py-4'>
        <div className='p-2 border bdr flex-c justify-between' onClick={() => setOpen(!open)}>
          <span className='font-bold'>
            <Trans>Formatting Options</Trans>
          </span>
          <ChevronSmallDown className='shrink-0' />
        </div>
        {open && (
          <div className='p-2 border-b border-l border-r bdr'>
            <FieldNumber />
          </div>
        )}
      </div>
    </div>
  )
}
